import React, { useMemo, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'sonner';
import { Building2, Calendar, DollarSign, FileText, Lock, Pencil, Plus, Search, Trash2, Wallet, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { formatCurrency, cn } from '../../../lib/utils';
import api, { contratosApi } from '../../../lib/api';
import { formatDate } from '../../../lib/utils';
import { formatMoneyInput, parseMoneyInput, getErrorMsg } from './helpers';
import { useTableSort, compareValues, SortHeader } from './tableSort';

// ────────────────────────────────────────────────────────────────────────────
// Constantes de la estructura Torre Emilia (Fase 3/4)
// ────────────────────────────────────────────────────────────────────────────
const CONJUNTO_PISO1 = 'conj_piso1_te';
const CONJUNTO_SUBSUELO = 'conj_subsuelo_te';
const SUBSUELO_VIRTUAL_ID = '__conj_subsuelo_te__'; // ID virtual para el conjunto en el selector

// Próxima actualización del contrato (columna "Actualización" de 3. Contratos).
// Sale de fecha_inicio + ajuste_frecuencia (el campo correcto para el calendario de ajustes).
const FREQ_MESES_TE = { mensual: 1, bimestral: 2, trimestral: 3, cuatrimestral: 4, semestral: 6, anual: 12 };
const MESES_ES_TE = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
// El calendario cuenta desde el último aumento real confirmado a mano (si es posterior al inicio).
const fechaAnclaAjuste = (c) => {
  const fi = (c.fecha_inicio || '').slice(0, 10);
  return c._ultimo_aumento && c._ultimo_aumento > fi.slice(0, 7) ? `${c._ultimo_aumento}-01` : fi;
};
function proximaActualizacionLabel(c) {
  const tipoRaw = (c.indice_tipo || '').toLowerCase().replace(/ /g, '_');
  const esSin = (t) => ['sin_ajuste', 'ninguno', 'no_ajusta'].includes(t);
  const sinAjuste = esSin(tipoRaw) ||
    (c.ajuste_indices || []).some(i => esSin((i.tipo || '').toLowerCase().replace(/ /g, '_')));
  if (sinAjuste) return '—';
  const fi = fechaAnclaAjuste(c);
  const freqStr = (c.ajuste_frecuencia || c.indice_frecuencia || '').toLowerCase();
  const freq = FREQ_MESES_TE[freqStr];
  if (!fi || !freq) return '—';
  const [y, m] = fi.split('-').map(Number);
  if (!y || !m) return '—';
  const now = new Date();
  const curY = now.getFullYear(), curM = now.getMonth() + 1;
  const monthsFromStart = (curY - y) * 12 + (curM - m);
  if (monthsFromStart < 0) return '—'; // contrato que empieza en el futuro
  if (monthsFromStart > 0 && monthsFromStart % freq === 0) return 'Este mes';
  let k = Math.ceil(monthsFromStart / freq);
  if (k === 0) k = 1; // primer ajuste tras el inicio
  const nextDate = new Date(y, (m - 1) + k * freq, 1);
  if (nextDate.getFullYear() === curY && nextDate.getMonth() + 1 === curM) return 'Este mes';
  return MESES_ES_TE[nextDate.getMonth()];
}

// Valor numérico ordenable de la "próxima actualización" (timestamp del próximo
// ajuste). Sin ajuste / sin datos → Infinity (van al final en orden ascendente).
function proximaActualizacionSortValue(c) {
  const tipoRaw = (c.indice_tipo || '').toLowerCase().replace(/ /g, '_');
  const esSin = (t) => ['sin_ajuste', 'ninguno', 'no_ajusta'].includes(t);
  const sinAjuste = esSin(tipoRaw) ||
    (c.ajuste_indices || []).some(i => esSin((i.tipo || '').toLowerCase().replace(/ /g, '_')));
  if (sinAjuste) return Infinity;
  const fi = fechaAnclaAjuste(c);
  const freqStr = (c.ajuste_frecuencia || c.indice_frecuencia || '').toLowerCase();
  const freq = FREQ_MESES_TE[freqStr];
  if (!fi || !freq) return Infinity;
  const [y, m] = fi.split('-').map(Number);
  if (!y || !m) return Infinity;
  const now = new Date();
  const curY = now.getFullYear(), curM = now.getMonth() + 1;
  const monthsFromStart = (curY - y) * 12 + (curM - m);
  if (monthsFromStart < 0) return new Date(y, m - 1, 1).getTime();
  if (monthsFromStart > 0 && monthsFromStart % freq === 0) return new Date(curY, curM - 1, 1).getTime();
  let k = Math.ceil(monthsFromStart / freq);
  if (k === 0) k = 1;
  return new Date(y, (m - 1) + k * freq, 1).getTime();
}

// ────────────────────────────────────────────────────────────────────────────
// Reglas de "alquilable" para Torre Emilia
// ────────────────────────────────────────────────────────────────────────────
function buildCatalogoAlquilable(unidades) {
  // Mapas auxiliares
  const byId = Object.fromEntries(unidades.map(u => [u.unidad_id, u]));

  const piso1Members = unidades.filter(u => u.conjunto_alquilable_id === CONJUNTO_PISO1);
  const subsueloMembers = unidades.filter(u => u.conjunto_alquilable_id === CONJUNTO_SUBSUELO);

  // Piso 1 administrado si CUALQUIERA del bloque tiene en_administracion=true
  // (UI mantiene atomicidad: toggle del bloque pone todo o nada)
  const piso1Admin = piso1Members.some(u => u.en_administracion);
  const subsueloAdmin = subsueloMembers.some(u => u.en_administracion);

  // ── Sección A: Oficinas/Locales principales ──
  // Se incluyen TODAS, pero las que NO están en administración se marcan (_noAdmin)
  // para mostrarlas deshabilitadas (con candado) y NO permitir cargarles contrato.
  const principalesAdmin = unidades.filter(u =>
    (u.tipo === 'oficina' || u.tipo === 'local') &&
    u.rol_unidad !== 'componente' &&
    !u.conjunto_alquilable_id && // excluye los del Piso 1
    u.visible_en_listado_unidades !== false
  ).map(u => ({ ...u, _noAdmin: u.en_administracion !== true }))
    .sort((a, b) => (a._noAdmin ? 1 : 0) - (b._noAdmin ? 1 : 0));

  // ── Sección B: Bloque Piso 1 ──
  // Solo si está administrado completo (basta con que el bloque esté ON).
  // Mostramos sus piezas individuales (O101..O105, W001..W026, C007, C008).
  const piso1Items = piso1Admin ? [...piso1Members] : [];

  // ── Sección C: Cocheras componentes (asociadas a una principal admin) ──
  const cocherasComponentes = unidades.filter(u => {
    if (u.rol_unidad !== 'componente' || u.tipo !== 'cochera') return false;
    const principal = byId[u.unidad_principal_id];
    return !!principal && principal.en_administracion === true;
  });

  // ── Sección D: Cocheras sueltas independientes ──
  // Se incluyen todas; las no administradas quedan deshabilitadas (_noAdmin).
  const cocherasSueltas = unidades.filter(u =>
    u.tipo === 'cochera' &&
    u.rol_unidad !== 'componente' &&
    (!u.conjunto_alquilable_id || u.conjunto_alquilable_id === CONJUNTO_SUBSUELO)
  ).map(u => ({ ...u, _noAdmin: u.en_administracion !== true }))
    .sort((a, b) => (a._noAdmin ? 1 : 0) - (b._noAdmin ? 1 : 0));

  // ── Sección E: Conjuntos alquilables ──
  // El bloque "Cocheras Subsuelo" ya NO se ofrece como conjunto seleccionable en contratos:
  // sus cocheras aparecen individualmente (ver cocherasSueltas). El agrupamiento
  // conjunto_alquilable_id="conj_subsuelo_te" se MANTIENE intacto en los datos para
  // expensas, cobros y vistas de propietarios.
  const conjuntos = [];

  return {
    principales: principalesAdmin,
    piso1: piso1Items,
    piso1Admin,
    cocherasComponentes,
    cocherasSueltas,
    conjuntos,
    byId,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Form inicial mínimo para contrato TE
// ────────────────────────────────────────────────────────────────────────────
const initialContratoFormTE = {
  razon_social: '',
  nombre_comercial: '',
  cuit: '',
  fecha_inicio: '',
  fecha_fin: '',
  monto_inicial: '',
  moneda: 'ARS',
  indice_tipo: 'ipc',
  indice_frecuencia: 'trimestral',
  contacto_admin_nombre: '',
  contacto_admin_email: '',
};

export const AlquileresTorreEmiliaView = forwardRef(({
  edificio,
  unidades,
  contratos,
  depositos,
  fetchData,
  onReloadUnidades,
  isComercial,
  verazResumen = {},
  onOpenNuevoContratoMulti, // NUEVO: callback hacia el padre para abrir el dialog completo con multi-unidad
  onOpenEditContrato,       // (contrato) => abre el modal de Edición con todos los datos precargados
  onOpenDeleteContrato,     // (contrato) => abre el AlertDialog de confirmación de borrado
  onOpenDeposito,           // (unidad, contrato) => abre modal "Registrar Depósito en Garantía"
  onOpenDetalleDeposito,    // (deposito) => abre modal "Ver/Editar Garantía"
}, ref) => {
  const [openNuevo, setOpenNuevo] = useState(false);
  const [seleccion, setSeleccion] = useState([]); // [{kind:'unidad'|'conjunto', id, nombre, tipo, miembros?}]
  const [busqueda, setBusqueda] = useState('');
  const [form, setForm] = useState(initialContratoFormTE);
  const [saving, setSaving] = useState(false);
  const [anclas, setAnclas] = useState({});
  useEffect(() => {
    if (!edificio?.edificio_id) return;
    api.get(`/facturacion/${edificio.edificio_id}/ultimo-aumento`).then(r => setAnclas(r.data || {})).catch(() => setAnclas({}));
  }, [edificio?.edificio_id, contratos]);

  const catalogo = useMemo(() => buildCatalogoAlquilable(unidades), [unidades]);

  // ── Orden canónico de unidades (igual que 5.Factura / 1.Unidades):
  // Piso 1 primero, luego oficinas por número (201..402), y al final Piso 5, 6, 7.
  // La posición del contrato la define su unidad principal (mejor oficina). ──
  const ordenContrato = useMemo(() => {
    const tipoPri = { oficina: 0, cowork: 1, local: 2, cochera: 3 };
    const byId = Object.fromEntries(unidades.map(u => [u.unidad_id, u]));
    const ordenUnidad = (uid) => {
      const u = byId[uid];
      if (!u) return [99, 3, 9999];
      const tp = tipoPri[u.tipo] ?? 99;
      const n = String(u.nombre_unidad || '').trim();
      if (/^piso\s*1\b/i.test(n)) return [tp, 0, 0];
      const soloNum = n.match(/^(\d+)/);
      if (soloNum) return [tp, 1, parseInt(soloNum[1], 10)];
      const mPiso = n.match(/piso\s*(\d+)/i);
      if (mPiso) return [tp, 2, parseInt(mPiso[1], 10)];
      return [tp, 3, 9999];
    };
    const cmpArr = (a, b) => {
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return a[i] - b[i]; }
      return 0;
    };
    return (c) => {
      const ucs = (c.unidades_contrato || []).map(x => x.unidad_id);
      if (c.unidad_id && !ucs.includes(c.unidad_id)) ucs.push(c.unidad_id);
      // principal = la unidad con mejor (menor) orden canónico.
      let best = null;
      for (const uid of ucs) {
        const o = ordenUnidad(uid);
        if (!best || cmpArr(o, best) < 0) best = o;
      }
      return best || [99, 3, 9999];
    };
  }, [unidades]);

  // ── Métricas (idénticas al flujo legacy) ──
  // Los contratos legacy sobre unidades de COWORK no van en la vista Oficina (el cowork
  // se maneja por suscriptores). Se excluye un contrato si TODAS sus unidades son cowork.
  const coworkUnitIds = useMemo(
    () => new Set((unidades || []).filter(u => u.tipo === 'cowork').map(u => u.unidad_id)),
    [unidades]
  );
  const esContratoCowork = useMemo(() => (c) => {
    const ids = (c.unidades_contrato || []).map(x => x.unidad_id);
    if (c.unidad_id) ids.push(c.unidad_id);
    return ids.length > 0 && ids.every(id => coworkUnitIds.has(id));
  }, [coworkUnitIds]);
  const contratosVigentes = useMemo(
    () => contratos
      .filter(c => c.estado === 'vigente' && !esContratoCowork(c))
      .sort((a, b) => {
        const oa = ordenContrato(a), ob = ordenContrato(b);
        return oa[0] - ob[0] || oa[1] - ob[1] || oa[2] - ob[2];
      }),
    [contratos, ordenContrato, esContratoCowork]
  );
  const hoy = new Date();
  const en30 = new Date(hoy); en30.setDate(en30.getDate() + 30);
  const en90 = new Date(hoy); en90.setDate(en90.getDate() + 90);
  const vencen30 = contratosVigentes.filter(c => {
    const f = c.fecha_fin && new Date(c.fecha_fin);
    return f && f <= en30 && f >= hoy;
  }).length;
  const renovar90 = contratosVigentes.filter(c => {
    const f = c.fecha_fin && new Date(c.fecha_fin);
    return f && f <= en90 && f > en30;
  }).length;
  const pasivoARS = contratosVigentes
    .filter(c => c.moneda === 'ARS')
    .reduce((sum, c) => sum + (parseFloat(c.monto_actual || c.monto_inicial) || 0), 0);
  const pasivoUSD = contratosVigentes
    .filter(c => c.moneda === 'USD')
    .reduce((sum, c) => sum + (parseFloat(c.monto_actual || c.monto_inicial) || 0), 0);

  // ── Ordenamiento por columnas (no se persiste; default = por Unidades) ──
  const { sortConfig, requestSort } = useTableSort();
  const getSortValue = useCallback((c, key) => {
    switch (key) {
      case 'inquilino': {
        const isHumana = (c.tipo_inquilino || 'juridica') === 'humana';
        return (isHumana
          ? (c.firmante_nombre || c.razon_social || c.inquilino || '')
          : (c.nombre_comercial || c.razon_social || c.inquilino || '')).toLowerCase();
      }
      case 'numero':
        return c.numero_contrato ? String(c.numero_contrato) : '\uffff';
      case 'vencimiento':
        return c.fecha_fin ? new Date(c.fecha_fin).getTime() : Infinity;
      case 'monto':
        return parseFloat(c.monto_actual || c.monto_inicial) || 0;
      case 'actualizacion':
        return proximaActualizacionSortValue({ ...c, _ultimo_aumento: anclas[c.contrato_id] });
      case 'docs':
        return (c.etapas_contractuales || []).reduce((s, e) => s + ((e.archivos || []).length), 0);
      case 'garantia': {
        const dep = (depositos || []).find(d => d.contrato_id === c.contrato_id && d.estado === 'vigente');
        if (dep) return 0;
        if (c.garantia_no_disponible) return 1;
        return 2;
      }
      default:
        return 0;
    }
  }, [depositos, anclas]);

  const contratosOrdenados = useMemo(() => {
    // contratosVigentes ya viene ordenado por unidad (ordenContrato) en ascendente.
    if (!sortConfig.key || sortConfig.key === 'unidades') {
      return sortConfig.direction === 'desc'
        ? [...contratosVigentes].reverse()
        : contratosVigentes;
    }
    return [...contratosVigentes].sort((a, b) => {
      const cmp = compareValues(getSortValue(a, sortConfig.key), getSortValue(b, sortConfig.key), sortConfig.direction);
      if (cmp !== 0) return cmp;
      const oa = ordenContrato(a), ob = ordenContrato(b);
      return oa[0] - ob[0] || oa[1] - ob[1] || oa[2] - ob[2];
    });
  }, [contratosVigentes, sortConfig, getSortValue, ordenContrato]);

  // ── Helpers de selección ──
  const isUnidadSelected = (id) => seleccion.some(s => s.kind === 'unidad' && s.id === id);
  const isConjuntoSelected = (id) => seleccion.some(s => s.kind === 'conjunto' && s.id === id);
  const subsueloEstaSeleccionado = isConjuntoSelected(SUBSUELO_VIRTUAL_ID);

  const toggleUnidad = (u) => {
    // Si Subsuelo está seleccionado, no se permite agregar otras unidades.
    if (subsueloEstaSeleccionado) {
      toast.error('Si seleccionás "Cocheras Subsuelo", no podés combinarlo con otras unidades en el mismo contrato.');
      return;
    }
    setSeleccion(prev => {
      if (prev.some(s => s.kind === 'unidad' && s.id === u.unidad_id)) {
        return prev.filter(s => !(s.kind === 'unidad' && s.id === u.unidad_id));
      }
      return [...prev, { kind: 'unidad', id: u.unidad_id, nombre: u.nombre_unidad, tipo: u.tipo, rol_unidad: u.rol_unidad }];
    });
  };

  const toggleConjunto = (c) => {
    setSeleccion(prev => {
      if (prev.some(s => s.kind === 'conjunto' && s.id === c.virtual_id)) {
        return prev.filter(s => !(s.kind === 'conjunto' && s.id === c.virtual_id));
      }
      // Al seleccionar conjunto, limpiamos cualquier unidad ya seleccionada.
      if (prev.length > 0) {
        toast.info('Limpiamos otras unidades seleccionadas: el conjunto Subsuelo se contrata solo.');
      }
      return [{ kind: 'conjunto', id: c.virtual_id, nombre: c.nombre, miembros: c.miembros }];
    });
  };

  const clearSeleccion = () => setSeleccion([]);

  // ── Filtros buscador ──
  const filtra = (u) => {
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase().trim();
    return (u.nombre_unidad || '').toLowerCase().includes(q);
  };

  // ── Abrir modal con form limpio ──
  // NUEVO COMPORTAMIENTO: delega al padre `ContratosDepositos` para que abra el dialog
  // COMPLETO (mismo formulario que Foresta: Carga IA + Datos + Condiciones + Cochera +
  // Fiscal + Colppy + Comisión) con la solapa adicional "Unidades del contrato" como
  // primera tab. El catálogo de unidades/conjuntos alquilables se pasa al padre.
  const handleOpenNuevo = () => {
    if (typeof onOpenNuevoContratoMulti === 'function') {
      onOpenNuevoContratoMulti(catalogo);
      return;
    }
    // Fallback (no debería ocurrir): comportamiento legacy reducido.
    setSeleccion([]);
    setForm(initialContratoFormTE);
    setBusqueda('');
    setOpenNuevo(true);
  };

  // Expone openNuevo al padre (ContratosDepositos) para que pueda abrir el modal
  // desde el botón "Nuevo contrato" ubicado en la barra superior unificada.
  useImperativeHandle(ref, () => ({ openNuevo: handleOpenNuevo }), [catalogo, onOpenNuevoContratoMulti]);

  // ── Construir unidades_contrato y guardar ──
  const handleSave = async (e) => {
    e.preventDefault();
    if (seleccion.length === 0) {
      toast.error('Seleccioná al menos una unidad o conjunto.');
      return;
    }
    if (!form.razon_social) {
      toast.error('La razón social es obligatoria.');
      return;
    }
    if (!form.fecha_inicio || !form.fecha_fin) {
      toast.error('Las fechas de inicio y fin son obligatorias.');
      return;
    }
    const monto = parseFloat(String(form.monto_inicial).replace(/[^\d.-]/g, '')) || 0;
    if (monto <= 0) {
      toast.error('El monto inicial debe ser mayor a cero.');
      return;
    }

    // Expandimos conjuntos a sus unidades individuales
    let unidadesContrato = [];
    for (const sel of seleccion) {
      if (sel.kind === 'unidad') {
        unidadesContrato.push({ unidad_id: sel.id, monto: 0, tipo_cochera: null });
      } else if (sel.kind === 'conjunto') {
        for (const m of sel.miembros) {
          unidadesContrato.push({ unidad_id: m.unidad_id, monto: 0, tipo_cochera: m.tipo === 'cochera' ? 'estandar' : null });
        }
      }
    }

    // Si hay solo 1 unidad seleccionada, no es necesario unidades_contrato[]
    // (legacy soporta unidad_id solo). Mantenemos unidades_contrato siempre para consistencia.
    // Distribuimos el monto: la primera unidad lleva todo (el usuario puede editar luego).
    if (unidadesContrato.length > 0) {
      unidadesContrato[0].monto = monto;
    }

    const principalUnitId = unidadesContrato[0].unidad_id;
    const inquilinoDisplay = form.nombre_comercial || form.razon_social;

    const data = {
      unidad_id: principalUnitId,
      inquilino: inquilinoDisplay,
      nombre_comercial: form.nombre_comercial || null,
      razon_social: form.razon_social,
      fecha_inicio: form.fecha_inicio,
      fecha_fin: form.fecha_fin,
      monto_inicial: monto,
      monto_actual: null,
      fecha_carga_monto: new Date().toISOString().split('T')[0],
      moneda: form.moneda,
      indice_tipo: form.indice_tipo,
      indice_frecuencia: form.indice_tipo !== 'otro' ? form.indice_frecuencia : null,
      indice_valor: null,
      indice_condiciones: null,
      archivo_contrato_id: null,
      cuit: form.cuit || null,
      contacto_admin_nombre: form.contacto_admin_nombre || null,
      contacto_admin_email: form.contacto_admin_email || null,
      // Multi-unidad si hay más de 1
      unidades_contrato: unidadesContrato.length > 1 ? unidadesContrato : null,
      // Default comisión si es comercial
      porcentaje_comision: isComercial ? 50 : null,
    };

    setSaving(true);
    try {
      await contratosApi.create(data);
      const msg = seleccion.length === 1 && seleccion[0].kind === 'unidad'
        ? `Contrato creado para ${seleccion[0].nombre}`
        : seleccion.some(s => s.kind === 'conjunto')
          ? `Contrato creado para ${seleccion[0].nombre} (${unidadesContrato.length} cocheras)`
          : `Contrato creado con ${unidadesContrato.length} unidades`;
      toast.success(msg);
      setOpenNuevo(false);
      await fetchData();
      onReloadUnidades && onReloadUnidades();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al crear contrato'));
    } finally {
      setSaving(false);
    }
  };

  // ── Render unidad principal (oficinas/locales) en la tabla ──
  const renderContratoRow = (c) => {
    // Lista de unidades incluidas en el contrato
    const unidadesContratos = c.unidades_contrato && c.unidades_contrato.length > 0
      ? c.unidades_contrato.map(uc => catalogo.byId[uc.unidad_id]).filter(Boolean)
      : [catalogo.byId[c.unidad_id]].filter(Boolean);
    const nombresUnidades = unidadesContratos.map(u => u.nombre_unidad);

    // Detectar si todas son del conjunto Subsuelo
    const todasSubsuelo = unidadesContratos.length === 10 &&
      unidadesContratos.every(u => u?.conjunto_alquilable_id === CONJUNTO_SUBSUELO);

    // Separar unidad principal (oficina/local/cowork) de las cocheras asociadas.
    const principales = unidadesContratos.filter(u => u && u.tipo !== 'cochera');
    const cocheras = unidadesContratos.filter(u => u && u.tipo === 'cochera');
    const cocheraNombres = cocheras.map(u => u.nombre_unidad);
    const cocheraShort = cocheraNombres.length > 2
      ? <>{cocheraNombres.slice(0, 2).join(', ')}<span className="ml-0.5">+{cocheraNombres.length - 2}</span></>
      : cocheraNombres.join(', ');
    const cocheraLine = cocheras.length > 0 ? (
      <div className="text-[11px] text-slate-500 leading-tight mt-0.5" data-testid={`te-cocheras-${c.contrato_id}`}>
        {cocheraShort}
      </div>
    ) : null;

    const labelUnidades = todasSubsuelo
      ? <><span className="font-medium">Cocheras Subsuelo</span><span className="text-slate-400 ml-1">(10 cocheras)</span></>
      : principales.length > 0
        ? (
          <div className="leading-tight">
            <div className="font-semibold text-slate-900">{principales.map(u => u.nombre_unidad).join(', ')}</div>
            {cocheraLine}
          </div>
        )
        : (
          <div className="leading-tight">
            <div className="font-semibold text-slate-900" data-testid={`te-cocheras-${c.contrato_id}`}>{cocheraShort}</div>
          </div>
        );

    // Semáforo: rojo=vencido o ≤30 días, amarillo=31-89 días, verde=≥90 días
    let semaforoClass = '';
    if (c.fecha_fin) {
      const dias = Math.ceil((new Date(c.fecha_fin) - hoy) / 86400000);
      if (dias <= 30) semaforoClass = 'bg-red-500';
      else if (dias <= 89) semaforoClass = 'bg-yellow-500';
      else semaforoClass = 'bg-emerald-500';
    }

    return (
      <tr key={c.contrato_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`te-contrato-row-${c.contrato_id}`}>
        <td className="py-2.5 px-4 text-sm text-slate-900 whitespace-nowrap" title={nombresUnidades.join(', ')}>{labelUnidades}</td>
        <td className="py-2.5 px-3 text-sm text-slate-700 max-w-[220px]">
          {(() => {
            const isHumana = (c.tipo_inquilino || 'juridica') === 'humana';
            const inqDisplay = isHumana
              ? (c.firmante_nombre || c.razon_social || c.inquilino || '—')
              : (c.nombre_comercial || c.razon_social || c.inquilino || '—');
            const inqTooltip = isHumana
              ? (c.firmante_nombre || c.razon_social || c.inquilino || '')
              : (c.razon_social || c.nombre_comercial || c.inquilino || '');
            return (
              <div className="leading-tight">
                <div className="truncate font-medium text-slate-900" title={inqTooltip || inqDisplay}>{inqDisplay}</div>
              </div>
            );
          })()}
        </td>
        <td className="py-2.5 px-3 text-sm text-slate-700">{c.numero_contrato || <span className="italic text-slate-400">Sin número</span>}</td>
        <td className="py-2.5 px-3 text-sm">
          <div className="flex items-center gap-2">
            {semaforoClass && <span className={`inline-block w-2 h-2 rounded-full ${semaforoClass}`} />}
            <span className="text-slate-700">{formatDate(c.fecha_fin)}</span>
          </div>
        </td>
        <td className="py-2.5 px-3 text-sm text-center" data-testid={`te-actualizacion-${c.contrato_id}`}>
          {(() => {
            const label = proximaActualizacionLabel({ ...c, _ultimo_aumento: anclas[c.contrato_id] });
            const esEsteMes = label === 'Este mes';
            return (
              <span className={cn(
                'text-xs font-medium',
                esEsteMes ? 'text-amber-700' : label === '—' ? 'text-slate-400' : 'text-slate-600'
              )}>
                {label}
              </span>
            );
          })()}
        </td>
        <td className="py-2.5 px-3 text-center">
          {(() => {
            const nDocs = (c.etapas_contractuales || []).reduce((s, e) => s + ((e.archivos || []).length), 0);
            return (
              <button
                type="button"
                onClick={() => onOpenEditContrato && onOpenEditContrato(c)}
                disabled={!onOpenEditContrato}
                className={`inline-flex items-center gap-1 text-xs hover:underline ${nDocs > 0 ? 'text-emerald-700' : 'text-slate-400'}`}
                data-testid={`te-documentos-${c.contrato_id}`}
                title="Ver/editar documentos del contrato"
              >
                <FileText className="w-3.5 h-3.5" />
                {nDocs}
              </button>
            );
          })()}
        </td>
        {/* Garantía: una sola por contrato (asociada al contrato_id). Multi-unidad usa la primera unidad como referencia para "Falta Garantía". */}
        <td className="py-2.5 px-3 text-center" data-testid={`te-garantia-cell-${c.contrato_id}`}>
          {(() => {
            const depositoVigente = (depositos || []).find(
              d => d.contrato_id === c.contrato_id && d.estado === 'vigente'
            );
            if (depositoVigente) {
              return (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onOpenDetalleDeposito && onOpenDetalleDeposito(depositoVigente)}
                  disabled={!onOpenDetalleDeposito}
                  data-testid={`te-garantia-ver-${c.contrato_id}`}
                >
                  Ver/Editar
                </Button>
              );
            }
            // Sin garantía → usar la primera unidad del contrato como referencia (igual que Foresta).
            const primeraUnidad = unidadesContratos[0];
            if (c.garantia_no_disponible) {
              return (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs text-slate-500 border-slate-300 hover:bg-slate-50"
                  onClick={() => onOpenDeposito && onOpenDeposito(primeraUnidad, c)}
                  disabled={!onOpenDeposito || !primeraUnidad}
                  data-testid={`te-garantia-no-disponible-${c.contrato_id}`}
                  title="Marcada como no disponible. Click para editar."
                >
                  No hay
                </Button>
              );
            }
            return (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => onOpenDeposito && onOpenDeposito(primeraUnidad, c)}
                disabled={!onOpenDeposito || !primeraUnidad}
                data-testid={`te-garantia-falta-${c.contrato_id}`}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Falta
              </Button>
            );
          })()}
        </td>
        <td className="py-2.5 px-3">
          <div className="flex items-center justify-end gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              title="Editar contrato"
              onClick={() => onOpenEditContrato && onOpenEditContrato(c)}
              data-testid={`te-contrato-edit-${c.contrato_id}`}
              disabled={!onOpenEditContrato}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              title="Eliminar contrato"
              onClick={() => onOpenDeleteContrato && onOpenDeleteContrato(c)}
              data-testid={`te-contrato-delete-${c.contrato_id}`}
              disabled={!onOpenDeleteContrato}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  // ── Render selector multi-unidad agrupado ──
  const renderSeccionTitulo = (label, count) => (
    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-4 mb-2">
      {label}
      <span className="ml-2 text-slate-400 normal-case font-medium">({count})</span>
    </h4>
  );

  const renderUnidadCheckbox = (u, extra = null) => {
    const sel = isUnidadSelected(u.unidad_id);
    const noAdmin = u._noAdmin === true;
    const disabled = subsueloEstaSeleccionado || noAdmin;
    return (
      <label
        key={u.unidad_id}
        title={noAdmin ? 'Sin administración. Activá la administración en la tab "2. Admin" para poder cargarle un contrato.' : undefined}
        className={cn(
          'flex items-center gap-2 px-2 py-1.5 rounded text-sm border',
          sel ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:bg-slate-50',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          noAdmin && 'bg-slate-50 hover:bg-slate-50'
        )}
        data-testid={`te-selector-unidad-${u.unidad_id}`}
      >
        {noAdmin ? (
          <Lock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-testid={`te-selector-lock-${u.unidad_id}`} />
        ) : (
          <Checkbox
            checked={sel}
            disabled={disabled}
            onCheckedChange={() => toggleUnidad(u)}
            data-testid={`te-selector-check-${u.unidad_id}`}
          />
        )}
        <span className={cn('font-medium', noAdmin ? 'text-slate-500' : 'text-slate-900')}>{u.nombre_unidad}</span>
        <span className="text-xs text-slate-500 capitalize ml-auto">{noAdmin ? 'sin administración' : u.tipo}</span>
        {extra && <span className="text-xs text-slate-400">{extra}</span>}
      </label>
    );
  };

  const renderConjuntoCheckbox = (c) => {
    const sel = isConjuntoSelected(c.virtual_id);
    return (
      <label
        key={c.virtual_id}
        className={cn(
          'flex items-center gap-2 px-2 py-2 rounded text-sm border-2 cursor-pointer',
          sel ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-slate-200 hover:bg-slate-50'
        )}
        data-testid={`te-selector-conjunto-${c.virtual_id}`}
      >
        <Checkbox
          checked={sel}
          onCheckedChange={() => toggleConjunto(c)}
          data-testid={`te-selector-conjunto-check-${c.virtual_id}`}
        />
        <div className="flex-1">
          <p className="font-semibold text-slate-900">{c.nombre}</p>
          <p className="text-xs text-slate-500">{c.helper}</p>
        </div>
        <span className="text-xs text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded">Conjunto</span>
      </label>
    );
  };

  const piso1FilteredOficinas = catalogo.piso1.filter(u => u.tipo === 'oficina').filter(filtra);
  const piso1FilteredCowork = catalogo.piso1.filter(u => u.tipo === 'cowork').filter(filtra);
  const piso1FilteredCocheras = catalogo.piso1.filter(u => u.tipo === 'cochera').filter(filtra);

  return (
    <>
      {/* ─── Tabla de contratos ─── */}
      {contratosVigentes.length === 0 ? (
        <div className="bg-slate-50 rounded-lg p-8 text-center" data-testid="te-alq-empty">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No hay contratos vigentes</p>
          <p className="text-sm text-slate-400 mt-1">
            Usá el botón "+ Nuevo contrato" para crear el primero. El selector mostrará solo unidades administradas por Global Citizen.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shrink-0" data-testid="te-alq-tabla">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <SortHeader label="Unidades" sortKey="unidades" sortConfig={sortConfig} onSort={requestSort} align="left" className="sticky top-0 z-20 bg-slate-50 text-left text-xs py-3 px-4 shadow-[0_1px_0_0_#e2e8f0]" />
                <SortHeader label="Inquilino" sortKey="inquilino" sortConfig={sortConfig} onSort={requestSort} align="left" className="sticky top-0 z-20 bg-slate-50 text-left text-xs py-3 px-3 shadow-[0_1px_0_0_#e2e8f0]" />
                <SortHeader label="N° Contrato" sortKey="numero" sortConfig={sortConfig} onSort={requestSort} align="left" className="sticky top-0 z-20 bg-slate-50 text-left text-xs py-3 px-3 shadow-[0_1px_0_0_#e2e8f0]" />
                <SortHeader label="Vencimiento" sortKey="vencimiento" sortConfig={sortConfig} onSort={requestSort} align="left" className="sticky top-0 z-20 bg-slate-50 text-left text-xs py-3 px-3 shadow-[0_1px_0_0_#e2e8f0]" />
                <SortHeader label="Actualización" sortKey="actualizacion" sortConfig={sortConfig} onSort={requestSort} align="center" className="sticky top-0 z-20 bg-slate-50 text-center text-xs py-3 px-3 shadow-[0_1px_0_0_#e2e8f0]" />
                <SortHeader label="Docs" sortKey="docs" sortConfig={sortConfig} onSort={requestSort} align="center" className="sticky top-0 z-20 bg-slate-50 text-center text-xs py-3 px-3 shadow-[0_1px_0_0_#e2e8f0]" />
                <SortHeader label="Garantía" sortKey="garantia" sortConfig={sortConfig} onSort={requestSort} align="center" className="sticky top-0 z-20 bg-slate-50 text-center text-xs py-3 px-3 shadow-[0_1px_0_0_#e2e8f0]" />
                <th className="sticky top-0 z-20 bg-slate-50 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3 shadow-[0_1px_0_0_#e2e8f0]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {contratosOrdenados.map(renderContratoRow)}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Modal Nuevo Contrato (Torre Emilia) ─── */}
      <Dialog open={openNuevo} onOpenChange={setOpenNuevo}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="te-alq-nuevo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Nuevo contrato — Torre Emilia
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Seleccioná las unidades del contrato. Solo aparecen las unidades administradas por Global Citizen.
            </p>
          </DialogHeader>

          <form onSubmit={handleSave} className="flex-1 overflow-y-auto -mx-6 px-6 pb-3">
            {/* ── Selector multi-unidad ── */}
            <section className="border border-slate-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900">1. Unidades del contrato</h3>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Buscar..."
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                      className="pl-7 w-44 h-8 text-xs"
                      data-testid="te-selector-busqueda"
                    />
                  </div>
                  {seleccion.length > 0 && (
                    <Button type="button" variant="outline" size="sm" onClick={clearSeleccion} className="h-8 text-xs">
                      <X className="w-3 h-3 mr-1" /> Limpiar ({seleccion.length})
                    </Button>
                  )}
                </div>
              </div>

              {/* Resumen de selección */}
              {seleccion.length > 0 && (
                <div className="bg-indigo-50 border border-indigo-100 rounded p-2 mb-3 text-xs" data-testid="te-selector-resumen">
                  <span className="font-medium text-indigo-900">Seleccionado:</span>{' '}
                  {seleccion.map(s => s.nombre).join(', ')}
                  {seleccion.some(s => s.kind === 'conjunto') && (
                    <span className="text-indigo-700 ml-1">(se expande a {seleccion[0].miembros.length} cocheras al guardar)</span>
                  )}
                </div>
              )}

              {/* Sin alquilables */}
              {catalogo.principales.length === 0 &&
               catalogo.piso1.length === 0 &&
               catalogo.cocherasComponentes.length === 0 &&
               catalogo.cocherasSueltas.length === 0 &&
               catalogo.conjuntos.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700" data-testid="te-selector-vacio">
                  No hay unidades administradas por GC en Torre Emilia. Primero activá la administración desde la tab "2. Admin".
                </div>
              )}

              {/* A. Oficinas/Locales principales */}
              {catalogo.principales.filter(filtra).length > 0 && (
                <>
                  {renderSeccionTitulo('A. Oficinas y locales principales', catalogo.principales.length)}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5" data-testid="te-selector-seccion-principales">
                    {catalogo.principales.filter(filtra).map(u => renderUnidadCheckbox(u))}
                  </div>
                </>
              )}

              {/* B. Piso 1 */}
              {catalogo.piso1Admin && (piso1FilteredOficinas.length + piso1FilteredCowork.length + piso1FilteredCocheras.length) > 0 && (
                <>
                  {renderSeccionTitulo('B. Piso 1 (bloque administrado)', catalogo.piso1.length)}
                  <div className="space-y-2" data-testid="te-selector-seccion-piso1">
                    {piso1FilteredOficinas.length > 0 && (
                      <div>
                        <p className="text-[11px] text-slate-500 mb-1">Oficinas operativas:</p>
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-1.5">
                          {piso1FilteredOficinas.map(u => renderUnidadCheckbox(u))}
                        </div>
                      </div>
                    )}
                    {piso1FilteredCowork.length > 0 && (
                      <div>
                        <p className="text-[11px] text-slate-500 mb-1">Puestos cowork:</p>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
                          {piso1FilteredCowork.map(u => renderUnidadCheckbox(u))}
                        </div>
                      </div>
                    )}
                    {piso1FilteredCocheras.length > 0 && (
                      <div>
                        <p className="text-[11px] text-slate-500 mb-1">Cocheras del Piso 1:</p>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
                          {piso1FilteredCocheras.map(u => renderUnidadCheckbox(u))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* C. Cocheras asociadas */}
              {catalogo.cocherasComponentes.filter(filtra).length > 0 && (
                <>
                  {renderSeccionTitulo('C. Cocheras asociadas a oficinas/locales', catalogo.cocherasComponentes.length)}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5" data-testid="te-selector-seccion-componentes">
                    {catalogo.cocherasComponentes.filter(filtra).map(u => {
                      const principal = catalogo.byId[u.unidad_principal_id];
                      return renderUnidadCheckbox(u, principal ? `← ${principal.nombre_unidad}` : null);
                    })}
                  </div>
                </>
              )}

              {/* D. Cocheras sueltas */}
              {catalogo.cocherasSueltas.filter(filtra).length > 0 && (
                <>
                  {renderSeccionTitulo('D. Cocheras sueltas independientes', catalogo.cocherasSueltas.length)}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5" data-testid="te-selector-seccion-sueltas">
                    {catalogo.cocherasSueltas.filter(filtra).map(u => renderUnidadCheckbox(u))}
                  </div>
                </>
              )}

              {/* E. Conjuntos */}
              {catalogo.conjuntos.length > 0 && (
                <>
                  {renderSeccionTitulo('E. Conjuntos alquilables', catalogo.conjuntos.length)}
                  <div className="space-y-2" data-testid="te-selector-seccion-conjuntos">
                    {catalogo.conjuntos.map(renderConjuntoCheckbox)}
                  </div>
                </>
              )}
            </section>

            {/* ── Datos del contrato ── */}
            <section className="border border-slate-200 rounded-lg p-4 mb-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">2. Datos del contrato</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Razón social *</Label>
                  <Input value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} required data-testid="te-form-razon-social" />
                </div>
                <div>
                  <Label className="text-xs">Nombre comercial</Label>
                  <Input value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} data-testid="te-form-nombre-comercial" />
                </div>
                <div>
                  <Label className="text-xs">CUIT</Label>
                  <Input value={form.cuit} onChange={(e) => setForm({ ...form, cuit: e.target.value })} placeholder="30-12345678-9" data-testid="te-form-cuit" />
                </div>
                <div>
                  <Label className="text-xs">Contacto admin (nombre)</Label>
                  <Input value={form.contacto_admin_nombre} onChange={(e) => setForm({ ...form, contacto_admin_nombre: e.target.value })} data-testid="te-form-contacto-nombre" />
                </div>
                <div>
                  <Label className="text-xs">Email admin</Label>
                  <Input type="email" value={form.contacto_admin_email} onChange={(e) => setForm({ ...form, contacto_admin_email: e.target.value })} data-testid="te-form-contacto-email" />
                </div>
              </div>
            </section>

            {/* ── Condiciones ── */}
            <section className="border border-slate-200 rounded-lg p-4 mb-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">3. Condiciones económicas</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Fecha inicio *</Label>
                  <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} required data-testid="te-form-fecha-inicio" />
                </div>
                <div>
                  <Label className="text-xs">Fecha fin *</Label>
                  <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} min={form.fecha_inicio || undefined} required data-testid="te-form-fecha-fin" />
                </div>
                <div>
                  <Label className="text-xs">Moneda</Label>
                  <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                    <SelectTrigger data-testid="te-form-moneda"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ARS">ARS</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Monto inicial *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                      {form.moneda === 'USD' ? 'US$' : '$'}
                    </span>
                    <Input
                      type="text"
                      value={formatMoneyInput(form.monto_inicial)}
                      onChange={(e) => setForm({ ...form, monto_inicial: parseMoneyInput(e.target.value) })}
                      placeholder="0"
                      className="pl-10 text-right"
                      required
                      data-testid="te-form-monto"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Índice actualización</Label>
                  <Select value={form.indice_tipo} onValueChange={(v) => setForm({ ...form, indice_tipo: v })}>
                    <SelectTrigger data-testid="te-form-indice"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ipc">IPC</SelectItem>
                      <SelectItem value="porcentaje">Porcentaje</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.indice_tipo !== 'otro' && (
                  <div>
                    <Label className="text-xs">Frecuencia</Label>
                    <Select value={form.indice_frecuencia} onValueChange={(v) => setForm({ ...form, indice_frecuencia: v })}>
                      <SelectTrigger data-testid="te-form-frecuencia"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mensual">Mensual</SelectItem>
                        <SelectItem value="bimestral">Bimestral</SelectItem>
                        <SelectItem value="trimestral">Trimestral</SelectItem>
                        <SelectItem value="cuatrimestral">Cuatrimestral</SelectItem>
                        <SelectItem value="semestral">Semestral</SelectItem>
                        <SelectItem value="anual">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </section>

            <DialogFooter className="sticky bottom-0 bg-white pt-3 border-t border-slate-200 -mx-6 px-6">
              <Button type="button" variant="outline" onClick={() => setOpenNuevo(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving || seleccion.length === 0} data-testid="te-form-save-btn">
                {saving ? 'Guardando...' : 'Crear contrato'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
});
AlquileresTorreEmiliaView.displayName = 'AlquileresTorreEmiliaView';
